import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Scissors, Scroll, Calendar, MapPin, Printer, CheckCircle, ClipboardText } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import PrintLabelsDialog from "@/components/PrintLabelsDialog";

const FINISH_REASONS = ["Order for remaining roll", "Expiring material", "Damaged roll"];


export default function RollDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthed } = useAuth();
  const [roll, setRoll] = useState(null);
  const [product, setProduct] = useState(null);
  const [cuts, setCuts] = useState([]);
  const [open, setOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [cutLen, setCutLen] = useState("");
  const [customer, setCustomer] = useState("");
  const [notes, setNotes] = useState("");
  const [finishReason, setFinishReason] = useState("");

  const load = async () => {
    const { data: r } = await api.get(`/rolls/${id}`);
    setRoll(r);
    const [{ data: p }, { data: c }] = await Promise.all([
      api.get(`/products/${r.product_id}`), api.get(`/rolls/${id}/cuts`),
    ]);
    setProduct(p); setCuts(c);
  };
  useEffect(() => { load(); }, [id]);

  // Scan-to-cut: when user lands here with ?action=cut and is authenticated,
  // auto-open the Record Cut dialog (typical flow when scanning a label QR).
  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "cut" && isAuthed && roll && roll.status === "active") {
      setOpen(true);
      // Strip the param so refreshing doesn't re-trigger
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, isAuthed, roll, setSearchParams]);

  if (!roll) return <div className="p-8 text-slate-500">Loading…</div>;

  const onReceive = async () => {
    try {
      await api.post(`/rolls/${id}/receive`);
      toast.success("Roll marked as received");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const onCut = async () => {
    try {
      await api.post(`/rolls/${id}/cut`, {
        length_m: Number(cutLen), customer, notes,
        finish_reason: isFinishing ? finishReason : undefined,
      });
      toast.success(`Cut ${cutLen}m recorded`);
      setOpen(false); setCutLen(""); setCustomer(""); setNotes(""); setFinishReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const isFinishing = roll && cutLen !== "" && Number(cutLen) > 0 && Number(cutLen) >= roll.current_length_m - 0.0001;

  const usedPct = ((roll.initial_length_m - roll.current_length_m) / roll.initial_length_m) * 100;
  const today = new Date();
  const expDate = new Date(roll.expiry_date);
  const daysUntilExp = Math.ceil((expDate - today) / 86400000);

  return (
    <div className="p-8 max-w-[1300px]">
      <Link to="/rolls" className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.15em] uppercase text-slate-500 hover:text-slate-900 mb-6" data-testid="back-rolls">
        <ArrowLeft size={14} /> Back to rolls
      </Link>

      <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-start mb-10">
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Roll · {roll.status}</div>
          <div className="flex items-center gap-3">
            {product && <div className="w-7 h-7 border border-slate-200 rounded-sm shrink-0" style={{ background: product.color_hex || "#e2e8f0" }} />}
            <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">{product?.name || roll.sku}</h1>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-slate-600 break-all">{roll.sku}</span>
            {product?.base_sku && (
              <>
                <span className="text-slate-300">·</span>
                <span className="font-mono text-xs text-slate-400">{product.base_sku}</span>
              </>
            )}
          </div>
          {roll.allocated_order && (
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-violet-800 bg-violet-50 border border-violet-200 rounded-md px-3 py-1.5" data-testid="roll-allocated-order">
              <ClipboardText size={16} weight="fill" />
              <span>Allocated to order <span className="font-semibold">{roll.allocated_order}</span> — consumed on arrival</span>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 border border-slate-200 bg-white mt-6">
            <div className="p-5 border-r border-slate-200">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Initial</div>
              <div className="font-mono text-2xl font-semibold mt-1">{roll.initial_length_m.toFixed(2)}m</div>
            </div>
            <div className="p-5 border-r border-slate-200">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Remaining</div>
              <div className="font-mono text-2xl font-semibold text-blue-600 mt-1">{roll.current_length_m.toFixed(2)}m</div>
            </div>
            <div className="p-5 border-r border-slate-200">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Width</div>
              <div className="font-mono text-2xl font-semibold mt-1">{roll.width_m.toFixed(2)}m</div>
            </div>
            <div className="p-5">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Used</div>
              <div className="font-mono text-2xl font-semibold mt-1">{usedPct.toFixed(0)}%</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4 h-2 w-full bg-slate-200 rounded-sm overflow-hidden">
            <div className="h-full bg-blue-600" style={{ width: `${Math.min(100, usedPct)}%` }} />
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-6 text-sm">
            <div className="border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-2 flex items-center gap-1.5"><Calendar size={12} /> Mfg Date</div>
              <div className="font-mono">{roll.manufacturing_date}</div>
            </div>
            <div className="border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Expires</div>
              <div className="font-mono">{roll.expiry_date}</div>
              <div className={`text-xs mt-1 ${daysUntilExp < 0 ? "text-red-600" : daysUntilExp < 90 ? "text-amber-600" : "text-slate-500"}`}>
                {daysUntilExp < 0 ? `Expired ${-daysUntilExp}d ago` : `${daysUntilExp}d remaining`}
              </div>
            </div>
            <div className="border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-2 flex items-center gap-1.5"><MapPin size={12} /> Location</div>
              <div className="font-mono">{roll.location || "—"}</div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {roll.status === "incoming" && isAuthed && (
              <Button onClick={onReceive} className="bg-emerald-600 hover:bg-emerald-700 gap-2 rounded-md" data-testid="receive-roll-btn">
                <CheckCircle size={16} weight="fill" /> Mark as Received
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button disabled={roll.current_length_m <= 0 || roll.status === "incoming"} className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="record-cut-btn">
                  <Scissors size={16} /> Record Cut
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Record cut from {roll.sku}</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Cut length (m) — max {roll.current_length_m.toFixed(2)}</Label>
                    <Input type="number" step="0.01" value={cutLen} onChange={(e) => setCutLen(e.target.value)} placeholder="e.g. 4.50" className="font-mono" data-testid="cut-length-input" autoFocus />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Customer / Job</Label>
                    <Input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="(optional)" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Notes</Label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
                  </div>
                  {isFinishing && (
                    <div className="space-y-2 border-t border-slate-100 pt-3" data-testid="finish-reason-group">
                      <Label className="text-xs font-mono tracking-[0.15em] uppercase text-amber-700">This cut finishes the roll — select a reason</Label>
                      <div className="grid grid-cols-1 gap-2">
                        {FINISH_REASONS.map((reason) => (
                          <button
                            key={reason}
                            type="button"
                            onClick={() => setFinishReason(reason)}
                            data-testid={`finish-reason-${reason.replace(/\s+/g, "-").toLowerCase()}`}
                            className={`text-left text-sm rounded-md border px-3 py-2 transition-colors ${finishReason === reason ? "border-blue-500 bg-blue-50/60 ring-1 ring-blue-500 text-slate-900 font-medium" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}
                          >
                            {reason}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setOpen(false); setFinishReason(""); }} className="rounded-md">Cancel</Button>
                  <Button onClick={onCut} disabled={!cutLen || Number(cutLen) <= 0 || (isFinishing && !finishReason)} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="cut-confirm-btn">Confirm Cut</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button onClick={() => setPrintOpen(true)} variant="outline" className="gap-2 rounded-md" data-testid="print-label-btn">
              <Printer size={16} /> Print Label
            </Button>
          </div>
        </div>

        {/* QR */}
        <div className="border border-slate-200 bg-white p-6 text-center">
          <QRCodeSVG value={roll.sku} size={160} level="M" />
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-3">Scan SKU</div>
        </div>
      </div>

      <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <Scroll size={18} /> Cut history
      </h2>
      {cuts.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
          No cuts recorded for this roll yet.
        </div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3 text-right">Length</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">By</th>
                <th className="px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {cuts.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-mono text-xs">{new Date(c.cut_at).toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-right tabular-nums font-semibold">−{c.length_m.toFixed(2)}m</td>
                  <td className="px-4 py-3">{c.customer || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{c.user_email}</td>
                  <td className="px-4 py-3 text-slate-600">{c.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PrintLabelsDialog open={printOpen} onOpenChange={setPrintOpen} rollIds={[id]} />
    </div>
  );
}
